import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, ArrowRight } from 'lucide-react';

export const AboutPage: React.FC = () => {
  return (
    <div className="space-y-16 py-12">
      
      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Who We Are
          </span>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark tracking-tight">
            About Us
          </h1>
          <p className="text-lg sm:text-xl font-serif italic text-slate-700">
            For The Truth is an online Christian Bookstore in India.
          </p>
        </div>
      </section>

      {/* 2. Core Ministry Mission Callout & Library Image */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="max-w-4xl mx-auto text-center sm:text-left">
          <p className="text-lg sm:text-xl font-serif text-slate-900 leading-relaxed">
            <strong className="font-extrabold uppercase tracking-wide text-editorial-dark">FOR THE TRUTH</strong> is a publishing ministry that exists to publish, distribute, and develop faithful biblical resources for the Church in India.
          </p>
        </div>

        {/* Featured Grand Library Panorama */}
        <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
          <img
            src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&q=80&w=1600"
            alt="For The Truth Library of Timeless Literature"
            className="w-full h-72 sm:h-96 md:h-[420px] object-cover"
          />
        </div>
      </section>

      {/* 3. Our Vision & Needs Analysis */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="space-y-3">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Context &amp; Need
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark">
            Our Vision
          </h2>
        </div>

        <div className="p-8 sm:p-10 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-4">
          <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
            There is a vast amount of faithful theological resources in English available across the globe. But the average Indian is unable to benefit from these resources for reasons such as: <strong className="text-slate-900 font-semibold">prohibitive cost</strong>, <strong className="text-slate-900 font-semibold">hard to find</strong>, <strong className="text-slate-900 font-semibold">difficult to procure</strong>, and <strong className="text-slate-900 font-semibold">inaccessible due to language and cultural differences</strong>.
          </p>
          <p className="text-slate-700 text-base sm:text-lg leading-relaxed">
            There is also a dearth of faithful resources created by and for the Indian church in English and various Indian vernaculars.
          </p>
        </div>
      </section>

      {/* 4. We Hope To (3 Core Strategic Pillars) */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="space-y-2">
          <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
            Our Strategic Goals
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark">
            We hope to:
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          <div className="p-6 sm:p-7 rounded-2xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 hover:shadow-editorial-lg transition-all space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold text-lg">
                1
              </div>
              <h3 className="font-serif text-lg font-bold text-slate-900">
                Publish Sound Titles
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Publish quality, and theologically sound titles in English for the Church in India.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-100 flex items-center gap-1.5 text-xs font-mono text-brand-700 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-brand-600" />
              <span>Editorial Integrity</span>
            </div>
          </div>

          <div className="p-6 sm:p-7 rounded-2xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 hover:shadow-editorial-lg transition-all space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold text-lg">
                2
              </div>
              <h3 className="font-serif text-lg font-bold text-slate-900">
                Affordable Distribution
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Distribute both classic and contemporary titles in English at affordable prices for the Church in India.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-100 flex items-center gap-1.5 text-xs font-mono text-brand-700 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-brand-600" />
              <span>Accessible Literature</span>
            </div>
          </div>

          <div className="p-6 sm:p-7 rounded-2xl bg-white border border-editorial-border shadow-xs hover:border-brand-300 hover:shadow-editorial-lg transition-all space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-200 flex items-center justify-center text-brand-700 font-mono font-bold text-lg">
                3
              </div>
              <h3 className="font-serif text-lg font-bold text-slate-900">
                Empower Indian Writers
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Develop, publish, and distribute English resources produced by Indian writers for the Church in India.
              </p>
            </div>
            <div className="pt-4 border-t border-slate-100 flex items-center gap-1.5 text-xs font-mono text-brand-700 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-brand-600" />
              <span>Indigenous Authorship</span>
            </div>
          </div>

        </div>
      </section>

      {/* 5. Author Partnership & Publishing Pathway */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 sm:p-12 rounded-2xl bg-editorial-dark text-white space-y-6 shadow-2xl border border-slate-800">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-400 font-bold">
              Author Submissions &amp; Publishing Services
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-100">
              Partner With For The Truth Publishing
            </h2>
            <p className="text-sm text-slate-300 leading-relaxed font-light">
              Are you an author, pastor, or theological educator with a manuscript or book proposal? We offer comprehensive developmental editing, bespoke typography, ISBN registration, and multi-channel print and digital distribution.
            </p>
          </div>

          <div className="pt-2 flex flex-wrap gap-4 text-xs font-medium">
            <Link
              to="/submit-enquiry"
              className="px-6 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold transition-all shadow-md inline-flex items-center gap-2"
            >
              <span>Submit Your Manuscript</span>
              <ArrowRight className="w-4 h-4" />
            </Link>

            <Link
              to="/our-process"
              className="px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-200 hover:text-white border border-slate-700 transition-all font-semibold"
            >
              Explore 12-Stage Process
            </Link>

            <a
              href="https://forthetruth.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-850 text-slate-300 hover:text-white border border-slate-800 transition-all font-semibold"
            >
              <span>Visit Bookstore (forthetruth.in)</span>
              <ExternalLink className="w-3.5 h-3.5 text-brand-400" />
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};
